import { RISK_LABELS, RISK_COLORS } from './safety-scoring';
import { RULE_LABELS } from './safety-data';
import type { ScoredEntity, SafetyAnalysisParams, RuleKey, RiskClass } from './safety-types';

// ── Distance helpers ──────────────────────────────────────────────────────

const KM_TO_MI = 0.621371;

function distDisplay(km: number, unit: 'km' | 'mi'): string {
  const val = unit === 'mi' ? km * KM_TO_MI : km;
  return Math.round(val).toLocaleString();
}
function distLabel(unit: 'km' | 'mi'): string { return unit === 'mi' ? 'mi' : 'km'; }

// ── Module state ───────────────────────────────────────────────────────────

const TABLE_PAGE_SIZE = 200;  // rows rendered at once; "Show all" lifts the cap

let _entities: ScoredEntity[]           = [];
let _params:   SafetyAnalysisParams | null = null;
let _sortCol:  string                   = 'score';
let _sortAsc:  boolean                  = true;
let _expandedIds = new Set<string>();
let _searchQuery = '';
let _showAll     = false;

// ── Entry point ────────────────────────────────────────────────────────────

export function renderSafetyResults(
  params:   SafetyAnalysisParams,
  entities: ScoredEntity[],
): void {
  _params      = params;
  _entities    = entities;
  _sortCol     = 'score';
  _sortAsc     = true;
  _expandedIds = new Set();
  _searchQuery = '';
  _showAll     = false;

  const container = document.getElementById('safetyResults')!;
  container.innerHTML = '';
  container.classList.remove('hidden');

  // Hide loading / error states
  document.getElementById('safetyLoading')!.classList.add('hidden');
  document.getElementById('safetyErrBox')! .classList.add('hidden');
  document.getElementById('safetyEmpty')!  .classList.add('hidden');

  renderKPIs(container);
  renderRiskChart(container);
  renderTopBottom(container);
  renderScorecardTable(container);

  // Show export button
  const btn = document.getElementById('safetyExportBtn') as HTMLElement | null;
  if (btn) btn.style.display = '';
}

// ── KPIs ──────────────────────────────────────────────────────────────────

function renderKPIs(container: HTMLElement): void {
  const scored = _entities.filter(e => e.totalScore !== null);
  const avgScore = scored.length
    ? Math.round(scored.reduce((s, e) => s + (e.totalScore ?? 0), 0) / scored.length * 10) / 10
    : 0;
  const totalDist   = _entities.reduce((s, e) => s + e.totalDistanceKm, 0);
  const totalEvents = _entities.reduce((s, e) => s + e.totalEvents, 0);
  const totalCount  = _entities.length;

  const sec = document.createElement('div');
  sec.innerHTML = `
    <div class="kpi-grid" style="grid-template-columns:repeat(4,1fr)">
      <div class="kpi-card kpi-hi">
        <div class="kpi-val" style="color:${scoreColor(avgScore)}">${avgScore}</div>
        <div class="kpi-lbl">Average Score</div>
        <div class="kpi-sub">${riskLabelFromScore(avgScore)}</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-val">${totalCount}</div>
        <div class="kpi-lbl">${_params?.scoreBy === 'driver' ? 'Drivers' : 'Vehicles'}</div>
        <div class="kpi-sub">${scored.length} scored · ${totalCount - scored.length} no activity</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-val">${fmtNum(Math.round(_params?.distUnit === 'mi' ? totalDist * KM_TO_MI : totalDist))}</div>
        <div class="kpi-lbl">Total Distance</div>
        <div class="kpi-sub">${distLabel(_params?.distUnit ?? 'km')} driven</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-val">${fmtNum(totalEvents)}</div>
        <div class="kpi-lbl">Total Events</div>
        <div class="kpi-sub">across selected rules</div>
      </div>
    </div>
  `.trim();
  container.appendChild(sec);
}

// ── Risk Distribution Chart ────────────────────────────────────────────────

function renderRiskChart(container: HTMLElement): void {
  const counts: Record<RiskClass, number> = { low: 0, mild: 0, medium: 0, high: 0, noActivity: 0 };
  _entities.forEach(e => { counts[e.riskClass]++; });

  const total = _entities.length || 1;
  const bands = (['low','mild','medium','high','noActivity'] as RiskClass[]).filter(k => counts[k] > 0);

  const rows = bands.map(cls => {
    const pct   = counts[cls] / total * 100;
    const color = RISK_COLORS[cls];
    return `
      <div class="sc2-risk-bar-row">
        <span class="sc2-risk-bar-label">${RISK_LABELS[cls]}</span>
        <div class="sc2-risk-bar-track">
          <div class="sc2-risk-bar-fill" style="width:${pct.toFixed(1)}%;background:${color};box-shadow:0 0 14px ${color}55;"></div>
        </div>
        <span class="sc2-risk-bar-count" style="color:${color}">${counts[cls]}</span>
      </div>
    `.trim();
  }).join('');

  const sec = document.createElement('div');
  sec.innerHTML = `
    <div class="sc2-chart-card">
      <div class="sc2-chart-title">Risk Distribution</div>
      ${rows}
    </div>
  `.trim();
  container.appendChild(sec);
}

// ── Top / Bottom Performers ────────────────────────────────────────────────

function renderTopBottom(container: HTMLElement): void {
  const scored = _entities.filter(e => e.totalScore !== null);
  if (scored.length === 0) return;

  const top    = [...scored].sort((a, b) => (b.totalScore ?? 0) - (a.totalScore ?? 0)).slice(0, 10);
  const bottom = [...scored].sort((a, b) => (a.totalScore ?? 0) - (b.totalScore ?? 0)).slice(0, 10);

  const makeRow = (e: ScoredEntity) => {
    const s = e.totalScore ?? 0;
    return `
      <div class="sc2-performer-row">
        <span class="sc2-performer-name" title="${escAttr(e.name)}">${escHtml(e.name)}</span>
        <span class="sc2-performer-score" style="color:${scoreColor(s)}">${s}</span>
        <span class="sc2-risk-badge risk-${e.riskClass}" style="margin-left:0.4rem;">${RISK_LABELS[e.riskClass]}</span>
      </div>`.trim();
  };

  const sec = document.createElement('div');
  sec.innerHTML = `
    <div class="sc2-performers-grid">
      <div class="sc2-performer-card">
        <div class="sc2-performer-header" style="color:var(--healthy)">▲ Top ${top.length}</div>
        ${top.map(makeRow).join('')}
      </div>
      <div class="sc2-performer-card">
        <div class="sc2-performer-header" style="color:var(--critical)">▼ Bottom ${bottom.length}</div>
        ${bottom.map(makeRow).join('')}
      </div>
    </div>
  `.trim();
  container.appendChild(sec);
}

// ── Scorecard Table ────────────────────────────────────────────────────────

function renderScorecardTable(container: HTMLElement): void {
  if (!_params) return;
  const enabled = _params.ruleConfigs;
  const label   = _params.scoreBy === 'driver' ? 'Driver' : 'Vehicle';

  const sec = document.createElement('div');
  sec.className = 'sc2-tbl-wrap-outer';

  const hdrBar = document.createElement('div');
  hdrBar.className = 'sc2-tbl-header-bar';
  hdrBar.innerHTML = `
    <div>
      <span class="sc2-tbl-title">Full Scorecard</span>
      <span class="sc2-tbl-meta" id="sc2TblMeta"></span>
    </div>
    <input type="text" class="sc2-search-input" id="sc2Search" placeholder="Search ${label.toLowerCase()}…">
  `.trim();
  sec.appendChild(hdrBar);

  const wrap = document.createElement('div');
  wrap.className = 'sc2-tbl-wrap';

  const tbl = document.createElement('table');
  tbl.className = 'sc2-tbl';
  tbl.id = 'sc2Table';

  const unit = _params?.distUnit ?? 'km';
  const cols: Array<{ key: string; label: string }> = [
    { key: 'expand', label: ''                       },
    { key: 'rank',   label: '#'                      },
    { key: 'name',   label: label                    },
    { key: 'score',  label: 'Score'                  },
    { key: 'risk',   label: 'Risk'                   },
    { key: 'dist',   label: `Dist ${distLabel(unit)}` },
    { key: 'events', label: 'Events'                 },
    ...enabled.map(r => ({ key: r.key, label: RULE_LABELS[r.key] })),
  ];

  const thead = tbl.createTHead();
  const hr    = thead.insertRow();
  cols.forEach(col => {
    const th = document.createElement('th');
    th.dataset.col = col.key;
    if (col.key === 'expand') {
      th.textContent = '';
      th.style.cssText = 'width:1.4rem;padding:0.5rem 0.3rem;cursor:default;';
      th.title = 'Click any row to expand per-rule event details';
    } else {
      th.textContent = col.label;
      if (col.key === _sortCol) th.className = _sortAsc ? 'sort-asc' : 'sort-desc';
      th.addEventListener('click', () => {
        if (_sortCol === col.key) _sortAsc = !_sortAsc;
        else { _sortCol = col.key; _sortAsc = col.key === 'score'; }
        renderTableBody(tbl);
      });
    }
    hr.appendChild(th);
  });

  wrap.appendChild(tbl);
  sec.appendChild(wrap);
  container.appendChild(sec);

  renderTableBody(tbl);

  document.getElementById('sc2Search')?.addEventListener('input', (e) => {
    _searchQuery = (e.target as HTMLInputElement).value.toLowerCase();
    renderTableBody(tbl);
  });
}

function renderTableBody(tbl: HTMLTableElement): void {
  if (!_params) return;
  const enabled = _params.ruleConfigs;

  tbl.querySelectorAll<HTMLTableCellElement>('th[data-col]').forEach(th => {
    th.className = '';
    if (th.dataset.col === _sortCol) th.className = _sortAsc ? 'sort-asc' : 'sort-desc';
  });

  const base = _entities.filter(e =>
    !_searchQuery || e.name.toLowerCase().includes(_searchQuery)
  );

  const sorted = [...base].sort((a, b) => {
    switch (_sortCol) {
      case 'name':   return _sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      case 'score': {
        const av = a.totalScore ?? -1, bv = b.totalScore ?? -1;
        return _sortAsc ? av - bv : bv - av;
      }
      case 'dist': {
        const av = a.totalDistanceKm, bv = b.totalDistanceKm;
        return _sortAsc ? av - bv : bv - av;
      }
      case 'events': {
        const av = a.totalEvents, bv = b.totalEvents;
        return _sortAsc ? av - bv : bv - av;
      }
      case 'risk': {
        const order: Record<RiskClass, number> = { low: 4, mild: 3, medium: 2, high: 1, noActivity: 0 };
        const av = order[a.riskClass], bv = order[b.riskClass];
        return _sortAsc ? av - bv : bv - av;
      }
      default: {
        const k  = _sortCol as RuleKey;
        const av = a.ruleScores[k] ?? -1, bv = b.ruleScores[k] ?? -1;
        return _sortAsc ? av - bv : bv - av;
      }
    }
  });

  const scoredSorted = sorted.filter(e => e.totalScore !== null);
  const rankMap = new Map(scoredSorted.map((e, i) => [e.id, i + 1]));

  const label  = _params!.scoreBy === 'driver' ? 'drivers' : 'vehicles';
  const metaEl = document.getElementById('sc2TblMeta');
  if (metaEl) metaEl.textContent = `${sorted.length} ${label}`;

  const existing = tbl.tBodies[0];
  if (existing) tbl.removeChild(existing);
  const tbody = tbl.createTBody();

  // Remove any previous "show all" footer
  tbl.parentElement?.querySelector('.sc2-show-all-row')?.remove();

  const unit = _params?.distUnit ?? 'km';
  const colCount = enabled.length + 7;

  const visible = (!_showAll && sorted.length > TABLE_PAGE_SIZE)
    ? sorted.slice(0, TABLE_PAGE_SIZE)
    : sorted;

  visible.forEach(entity => {
    const isExpanded = _expandedIds.has(entity.id);
    const tr  = tbody.insertRow();
    tr.className  = 'sc2-data-row';
    tr.dataset.id = entity.id;
    tr.title      = 'Click to see per-rule event details';

    const rank  = rankMap.get(entity.id) ?? '—';
    const score = entity.totalScore ?? null;
    const color = score !== null ? scoreColor(score) : '#9E9E9E';

    const cells = [
      // expand arrow cell
      `<span class="sc2-expand-arrow${isExpanded ? ' expanded' : ''}" aria-label="expand row">▶</span>`,
      `<span style="font-family:var(--mono);font-size:0.7rem;color:var(--muted);">${rank}</span>`,
      escHtml(entity.name),
      score !== null
        ? `<div class="sc2-score-wrap">
             <span class="sc2-score-num" style="color:${color}">${score}</span>
             <div class="sc2-score-bar" style="width:${Math.round(score * 0.6)}px;background:${color};opacity:0.7;"></div>
           </div>`
        : '<span style="color:var(--muted);font-size:0.7rem;">—</span>',
      `<span class="sc2-risk-badge risk-${entity.riskClass}">${RISK_LABELS[entity.riskClass]}</span>`,
      `<span style="font-family:var(--mono);font-size:0.72rem;">${distDisplay(entity.totalDistanceKm, unit)}</span>`,
      `<span style="font-family:var(--mono);font-size:0.72rem;">${entity.totalEvents.toLocaleString()}</span>`,
      ...enabled.map(r => {
        const s = entity.ruleScores[r.key];
        if (s === undefined || entity.totalScore === null) return '<span style="color:var(--muted);">—</span>';
        return `<span style="font-family:var(--mono);font-size:0.72rem;color:${scoreColor(s)};">${s}</span>`;
      }),
    ];

    cells.forEach(html => {
      const td = document.createElement('td');
      td.innerHTML = html;
      tr.appendChild(td);
    });

    tr.addEventListener('click', () => {
      const arrow = tr.querySelector<HTMLElement>('.sc2-expand-arrow');
      if (_expandedIds.has(entity.id)) {
        _expandedIds.delete(entity.id);
        tbl.querySelector(`tr[data-expand-id="${entity.id}"]`)?.remove();
        if (arrow) arrow.classList.remove('expanded');
      } else {
        _expandedIds.add(entity.id);
        insertExpandRow(tbody, tr, entity, colCount);
        if (arrow) arrow.classList.add('expanded');
      }
    });

    if (isExpanded) {
      insertExpandRow(tbody, tr, entity, colCount);
    }
  });

  // Show "load remaining" footer when capped
  if (!_showAll && sorted.length > TABLE_PAGE_SIZE) {
    const remaining = sorted.length - TABLE_PAGE_SIZE;
    const div = document.createElement('div');
    div.className = 'sc2-show-all-row';
    div.innerHTML = `
      <span style="color:var(--muted);font-size:0.72rem;">${remaining} more ${label} not shown</span>
      <button class="tb-btn" style="margin-left:0.75rem;">Show all ${sorted.length}</button>
    `.trim();
    div.querySelector('button')!.addEventListener('click', () => {
      _showAll = true;
      renderTableBody(tbl);
    });
    tbl.parentElement?.appendChild(div);
  }
}

function insertExpandRow(
  tbody:    HTMLTableSectionElement,
  afterRow: HTMLTableRowElement,
  entity:   ScoredEntity,
  colCount: number,
): void {
  if (!_params) return;
  const enabled = _params.ruleConfigs;

  const expTr = document.createElement('tr');
  expTr.className       = 'sc2-expand-row';
  expTr.dataset.expandId = entity.id;

  const td    = document.createElement('td');
  td.colSpan  = colCount;

  const inner = document.createElement('div');
  inner.className = 'sc2-expand-inner';

  enabled.forEach(rule => {
    const s    = entity.ruleScores[rule.key];
    const data = entity.byRule[rule.key];
    const c    = s !== undefined ? scoreColor(s) : '#9E9E9E';
    const barW = s !== undefined ? Math.round(s * 1.2) : 0;

    const row = document.createElement('div');
    row.className = 'sc2-expand-rule-row';
    row.innerHTML = `
      <span class="sc2-expand-rule-name">${escHtml(RULE_LABELS[rule.key])} (${rule.weight}%)</span>
      <span class="sc2-expand-rule-score" style="color:${c}">${s !== undefined ? s : '—'}</span>
      <div class="sc2-expand-rule-bar" style="width:${barW}px;background:${c};opacity:0.7;"></div>
      <span class="sc2-expand-rule-meta">${data.eventCount} event${data.eventCount !== 1 ? 's' : ''} · ${formatDetailedDistance(_params?.distUnit === 'mi' ? data.exceptionDistKm * KM_TO_MI : data.exceptionDistKm)} ${distLabel(_params?.distUnit ?? 'km')} during violation</span>
    `.trim();
    inner.appendChild(row);
  });

  const summary = document.createElement('div');
  summary.style.cssText = 'font-family:var(--mono);font-size:0.62rem;color:var(--muted);margin-top:0.25rem;';
  const u = _params?.distUnit ?? 'km';
  summary.textContent = `Total distance: ${distDisplay(entity.totalDistanceKm, u)} ${distLabel(u)} · Total events: ${entity.totalEvents.toLocaleString()}`;
  inner.appendChild(summary);

  td.appendChild(inner);
  expTr.appendChild(td);
  afterRow.insertAdjacentElement('afterend', expTr);
}

// ── Helpers ────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 90) return '#4CAF50';
  if (score >= 80) return '#FFC107';
  if (score >= 70) return '#FF9800';
  return '#F44336';
}

function riskLabelFromScore(score: number): string {
  if (score >= 90) return 'Low Risk Fleet';
  if (score >= 80) return 'Mild Risk Fleet';
  if (score >= 70) return 'Medium Risk Fleet';
  return 'High Risk Fleet';
}

function fmtNum(n: number): string { return n.toLocaleString(); }
function round1(n: number): number { return Math.round(n * 10) / 10; }
function formatDetailedDistance(n: number): string {
  return round1(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** Re-render existing results in a different display unit without recalculating scores. */
export function updateSafetyResultUnit(unit: 'km' | 'mi'): void {
  if (!_params || !_entities.length) return;
  _params = { ..._params, distUnit: unit };
  const container = document.getElementById('safetyResults');
  if (!container || container.classList.contains('hidden')) return;
  container.innerHTML = '';
  renderKPIs(container);
  renderRiskChart(container);
  renderTopBottom(container);
  renderScorecardTable(container);
}

function escHtml(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}
function escAttr(s: string): string {
  return escHtml(s).replace(/"/g,'&quot;');
}
